import { Component, input, ChangeDetectionStrategy, computed, signal, inject, LOCALE_ID } from '@angular/core';
import { CommonModule, formatDate } from '@angular/common';
import { ChartModule } from '@progress/kendo-angular-charts';
import { ButtonsModule } from '@progress/kendo-angular-buttons';
import { DialogsModule } from '@progress/kendo-angular-dialog';
import { SVGIcon } from '@progress/kendo-angular-icons';
import { moreVerticalIcon } from '@progress/kendo-svg-icons';
import DataTable from 'datatables.net-dt';
import { DatatableComponent } from 'src/app/core/components/datatable/datatable.component';
import { formatDateRangeLabel } from 'src/app/core/shared/common/date-range.util';
import { environment } from 'src/environments/environment';
import { VulnerabilityEventsResponse } from '../../../../../../../core/models/analytics/vss-models';

/** Plain text cells: DataTables writes raw HTML, and render.text() is its own escaper. */
const text = DataTable.render.text();

@Component({
  selector: 'app-vulnerability-events-chart',
  templateUrl: './vulnerability-events-chart.component.html',
  styleUrls: ['./vulnerability-events-chart.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [CommonModule, ChartModule, ButtonsModule, DialogsModule, DatatableComponent],
})
export class VulnerabilityEventsChartComponent {
  private locale = inject(LOCALE_ID);

  vulnerabilityEventsResponse = input.required<VulnerabilityEventsResponse>();

  public organizationId = input<number | null>();
  public startDate = input<string | null>();
  public endDate = input<string | null>();

  public moreVerticalIcon: SVGIcon = moreVerticalIcon;
  public showPopup = signal<boolean>(false);

  // Convert string dates to Date objects for proper chart formatting
  chartData = computed(() => {
    return this.vulnerabilityEventsResponse().events.map((event) => ({
      ...event,
      date: new Date(event.date),
    }));
  });

  public modalTitle = computed((): string => {
    const start = this.startDate();
    const end = this.endDate();
    const label = start && end ? formatDateRangeLabel([start, end]) : '';
    return label ? `Vulnerability Events (${label})` : 'Vulnerability Events';
  });

  public datatableUrl = computed((): string => {
    const id = this.organizationId();
    return id != null ? `${environment.apiUrl}/v1/vicarius/analytics/vulnerabilityeventsdetails(${id})` : '';
  });

  /** The same period the chart was computed for. */
  public periodParams = computed((): Record<string, string> => {
    const start = this.startDate();
    const end = this.endDate();
    return start && end
      ? { current_start: encodeURIComponent(start), current_end: encodeURIComponent(end) }
      : {};
  });

  private date = (value: string) => (value ? formatDate(value, 'MMM d, y, h:mm:ss a', this.locale) : '');

  /** The chart period is a UTC day: in local time it would show the day before. */
  private day = (value: string) => (value ? formatDate(value, 'MMM d, y', this.locale, 'UTC') : '');

  public datatableColumns = [
    { data: 'event_type', name: 'event_type', title: 'Event', type: 'string', render: text },
    { data: 'endpoint_name', name: 'endpoint_name', title: 'Endpoint | Device Name', type: 'string', render: text },
    { data: 'agent_id', name: 'agent_id', title: 'Endpoint ID', type: 'string', render: text },
    { data: 'cve_uid', name: 'cve_uid', title: 'CVE', type: 'string', render: text },
    { data: 'severity', name: 'severity', title: 'Severity', type: 'string', render: text },
    { data: 'event_at', name: 'event_at', title: 'Event At', type: 'string', render: this.date },
    { data: 'bucket_date', name: 'bucket_date', title: 'Trend Period', type: 'string', render: this.day },
  ];

  onViewData(): void {
    this.showPopup.set(true);
  }

  close(): void {
    this.showPopup.set(false);
  }
}
